import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { adminButton } from '@/components/admin/ui';
import {
  useUpdateSeries,
  type RecurringSeries,
  type SeriesChanges,
  type TeamMemberLite,
} from '@/hooks/useRecurringSeries';
import {
  FREQUENCY_OPTIONS,
  addMinutesToTime,
  describeFrequency,
  minutesBetween,
  nextOccurrences,
} from '@/lib/recurrence';
import { cn } from '@/lib/utils';
import { errorText } from './visit-edit';

const schema = z
  .object({
    frequency: z.enum(['weekly', 'fortnightly', 'four_weekly', 'monthly']),
    nextVisit: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Choose the date of the next visit.'),
    startTime: z.string().regex(/^\d{2}:\d{2}$/, 'Choose a start time.'),
    endTime: z.string().regex(/^\d{2}:\d{2}$/, 'Choose an end time.'),
    teamMemberIds: z.array(z.string()),
    endDate: z.string(),
    notes: z.string().max(2000, 'Keep notes under 2,000 characters.'),
  })
  .refine((v) => minutesBetween(v.startTime, v.endTime) >= 15, {
    path: ['endTime'],
    message: 'The visit needs to end at least 15 minutes after it starts.',
  })
  .refine((v) => !v.endDate || v.endDate >= v.nextVisit, {
    path: ['endDate'],
    message: 'The end date must be on or after the next visit.',
  });

type FormValues = z.infer<typeof schema>;

interface SeriesEditDialogProps {
  series: RecurringSeries | null;
  teamMembers: TeamMemberLite[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const today = () => format(new Date(), 'yyyy-MM-dd');

function defaultsFor(series: RecurringSeries): FormValues {
  const next =
    nextOccurrences({ frequency: series.frequency, anchorDate: series.anchor_date }, today(), 1)[0] ?? series.anchor_date;
  const start = series.start_time.slice(0, 5);
  return {
    frequency: series.frequency,
    nextVisit: next,
    startTime: start,
    endTime: addMinutesToTime(start, series.duration_minutes),
    teamMemberIds: series.team_member_ids ?? [],
    endDate: series.end_date ?? '',
    notes: series.notes ?? '',
  };
}

/** Edit a whole repeat clean from today onwards. */
export function SeriesEditDialog({ series, teamMembers, open, onOpenChange }: SeriesEditDialogProps) {
  const { toast } = useToast();
  const update = useUpdateSeries();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: series ? defaultsFor(series) : undefined,
  });

  useEffect(() => {
    if (series && open) form.reset(defaultsFor(series));
  }, [series, open, form]);

  if (!series) return null;

  const onSubmit = async (values: FormValues) => {
    const dirty = form.formState.dirtyFields;
    const changes: SeriesChanges = {};
    if (dirty.frequency) changes.frequency = values.frequency;
    // Only re-anchor when the owner picked a different next visit, or changed
    // frequency (so the new pattern counts from the visit they can see).
    if (dirty.nextVisit || dirty.frequency) changes.anchor_date = values.nextVisit;
    if (dirty.startTime) changes.start_time = values.startTime;
    if (dirty.startTime || dirty.endTime) changes.duration_minutes = minutesBetween(values.startTime, values.endTime);
    if (dirty.teamMemberIds) changes.team_member_ids = values.teamMemberIds;
    if (dirty.endDate) changes.end_date = values.endDate || null;
    if (dirty.notes) changes.notes = values.notes;
    if (Object.keys(changes).length === 0) {
      onOpenChange(false);
      return;
    }
    try {
      const result = await update.mutateAsync({ seriesId: series.id, changes });
      toast({
        title: 'Repeat clean updated',
        description: `Upcoming visits have been refreshed (${result?.created ?? 0} on the calendar from today). Visits you’d changed by hand stay as they are.`,
      });
      onOpenChange(false);
    } catch (error) {
      toast({ title: 'Couldn’t update the repeat clean', description: errorText(error), variant: 'destructive' });
    }
  };

  const freq = form.watch('frequency');
  const nextVisit = form.watch('nextVisit');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="series-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit repeat clean</DialogTitle>
          <DialogDescription>
            {series.client_name || series.title}. Changes apply from today; past, completed and hand-edited visits aren’t touched.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="adm-form space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="frequency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Repeats</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {FREQUENCY_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="nextVisit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Next visit</FormLabel>
                    <FormControl>
                      <Input type="date" min={today()} {...field} />
                    </FormControl>
                    {freq && /^\d{4}-\d{2}-\d{2}$/.test(nextVisit) && (
                      <FormDescription>{describeFrequency(freq, nextVisit)}</FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start time</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="endTime"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End time</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="teamMemberIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cleaners</FormLabel>
                  {teamMembers.length === 0 ? (
                    <p className="text-[12.5px] text-muted-foreground">Add team members to assign a cleaner.</p>
                  ) : (
                    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                      {teamMembers.map((m) => {
                        const checked = field.value.includes(m.id);
                        return (
                          <label
                            key={m.id}
                            className={cn(
                              'flex cursor-pointer items-center gap-2 rounded-lg border border-border px-2.5 py-2 text-[13px]',
                              checked && 'border-primary/40 bg-secondary/50',
                            )}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) =>
                                field.onChange(v ? [...field.value, m.id] : field.value.filter((id) => id !== m.id))
                              }
                              aria-label={m.name}
                            />
                            <span className="truncate">{m.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  <FormDescription>Leave everyone unticked to keep visits unassigned.</FormDescription>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="endDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Ends</FormLabel>
                  <FormControl>
                    <Input type="date" min={nextVisit || undefined} {...field} />
                  </FormControl>
                  <FormDescription>Last visit on or before this date. Leave blank to keep going.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes for each visit</FormLabel>
                  <FormControl>
                    <Textarea rows={3} placeholder="Key safe code, pets, priorities…" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" size="sm" className={adminButton.outline} onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className={adminButton.primary} disabled={update.isPending}>
                {update.isPending && <Loader2 className="animate-spin" />}
                Save changes
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
